const env = import.meta.env;

export interface EnvConfigurationModel {
  /** Build-time app id injected by `vite/create-app-config.ts` (`tenko`, `monokoro`, ...). */
  appId: string;
  env: string;
  /** Backend base URL including the `/api/v1` prefix. */
  apiUrl: string;
  socketUrl: string;
  /** Absolute URL of this app's login page; Google OAuth redirects back here. */
  loginPageUrl: string;
  /** Empty or absent disables Sentry for that app/mode. */
  sentryDsn?: string;
  /** PostHog project token. Empty or absent disables product analytics, flags and replay. */
  posthogKey?: string;
  /** PostHog ingestion host; US Cloud when empty. */
  posthogHost?: string;
  /** Short git commit injected by `vite/create-app-config.ts` (`''` when unknown). */
  appCommit: string;
  /** `true` under `vite dev` (`import.meta.env.DEV`). */
  isDev: boolean;
  /** Meta integrations: only dashboards that ship WhatsApp / Meta Ads set these. */
  facebookAppId?: string;
  whatsappEmbeddedSignupConfigId?: string;
  metaAdsConfigId?: string;
  /** Backend of the cards feature: `mock` runs the in-memory store, anything else the HTTP adapter. */
  cardsApi: 'mock' | 'http';
}

export const EnvConfiguration = (): EnvConfigurationModel => ({
  appId: env.VITE_APP_ID,
  env: env.VITE_ENV,
  apiUrl: env.VITE_API_URL,
  socketUrl: env.VITE_SOCKET_URL,
  loginPageUrl: env.VITE_LOGIN_PAGE_URL,
  sentryDsn: env.VITE_SENTRY_DSN,
  posthogKey: env.VITE_POSTHOG_KEY,
  posthogHost: env.VITE_POSTHOG_HOST,
  appCommit: env.VITE_APP_COMMIT ?? '',
  isDev: env.DEV,
  facebookAppId: env.VITE_FACEBOOK_APP_ID,
  whatsappEmbeddedSignupConfigId: env.VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID,
  metaAdsConfigId: env.VITE_META_ADS_CONFIG_ID,
  cardsApi: env.VITE_CARDS_API === 'mock' ? 'mock' : 'http',
});

export class ConfigService {
  private static instance: ConfigService;
  private readonly environment: EnvConfigurationModel;

  private constructor() {
    this.environment = EnvConfiguration();
  }

  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  get<K extends keyof EnvConfigurationModel>(key: K): EnvConfigurationModel[K] {
    return this.environment[key];
  }

  getOrThrow<K extends keyof EnvConfigurationModel>(
    key: K,
  ): NonNullable<EnvConfigurationModel[K]> {
    const value = this.environment[key];
    if (!value) {
      throw new Error(`Environment : ${key} not found or not loaded`);
    }
    return value;
  }
}
